<template>
  <div class="bg-cool-gray-100 text-sm text-gray-800 min-h-screen flex flex-col">
    <div v-if="authorized" class="flex flex-col flex-grow">
      <!-- Old navbar -->
      <navbar v-if="false" :title="titleBold" :title2="titleLight" :requireAuth="requireAuth">
        <template #navbarLogo>
          <!-- @slot Custom Logo element displayed in the navbar before the title. -->
          <slot name="navbarLogo" />
        </template>

        <template #default>
          <!-- @slot Set of navbar links -->
          <slot name="navbarLinks"></slot>
        </template>

        <template #navbarRight>
          <!-- @slot Custom component to include on the right side of the navbar. -->
          <slot name="navbarRight"></slot>
        </template>
      </navbar>

      <div class="flex flex-col flex-grow">
        <!-- @slot The body of the page. -->
        <slot />
      </div>
    </div>
    <div v-else class="flex flex-col flex-grow text-2xl justify-center items-center font-bold text-center">
      <div v-if="authInvalid">
        Invalid Token<br />
        <a :href="loginUrl" class="text-blue-400 text-lg">Login Again</a>
      </div>
      <div v-else>{{ authMessage }}</div>
    </div>

    <!-- @slot Custom footer. -->
    <slot name="footer">
      <div class="bg-gray-800 text-gray-100 p-4">
        <div class="text-center">&copy; {{ copyright }}</div>
      </div>
    </slot>

    <template v-if="authKeepAliveUrl">
      <iframe :src="authKeepAliveUrl" frameborder="0" height="0" width="0"></iframe>
    </template>
  </div>
</template>

<script>
/**
 * This is the default WorkLife layout wrapper that includes a top navbar and simple footer.
 */
import Navbar from '@/scripps-components/components/ui/Navbar.vue'
import cookies from 'js-cookie'
import axios from 'axios'
import auth from '../lib/auth.js'
import { mapActions } from 'vuex'

const baseDomain = location.hostname.split('.').slice(-2).join('.')
const apiDomain = import.meta.env.VITE_API_DOMAIN ?? `newsdesk-api.${baseDomain}`

export default {
  components: {
    Navbar
  },
  data() {
    return {
      isLoggedIn: false,
      authInvalid: false,
      baseDomain: 'scrippsnet.com',
      loginUrl: '',
      authMessage: '',
      authKeepAliveUrl: null,
      profile: {}
    }
  },
  props: {
    /**
     * The title that shows up in the navbar.
     */
    title: {
      type: String,
      default: null
    },
    /**
     * The second part of the navbar title in a lighter font.
     */
    title2: {
      type: String,
      default: null
    },
    /**
     * Indicate if we should require an authenticated employee before loading the content.
     */
    requireAuth: {
      type: Boolean,
      default: true
    }
  },
  emits: ['authVerified'],
  computed: {
    titleBold() {
      return this.title || 'Work'
    },
    titleLight() {
      return this.title || this.title2 ? this.title2 : 'Life'
    },
    copyright() {
      return `${new Date().getFullYear()} The E.W. Scripps Company. All rights reserved.`
    },
    authorized() {
      return this.isLoggedIn || !this.requireAuth
    }
  },
  mounted() {
    if (this.requireAuth) {
      this.authenticate()
    } else {
      // parse JWT to get user profile
      const token = cookies.get('EWS_AUTH_KEY')

      if (token) {
        const jwt = JSON.parse(atob(token.split('.')[1]))
        this.profile = jwt.data

        if (this.profile?.empid) {
          this.isLoggedIn = true
          this.$emit('authVerified', { data: jwt.data })
        }
      }
    }
  },
  created() {
    const prev = encodeURIComponent(location.href)
    if (import.meta.env.VITE_LOGIN_URL) this.loginUrl = import.meta.env.VITE_LOGIN_URL + `?prev=${prev}`
    else this.loginUrl = `https://${apiDomain}/okta/login?prev=${prev}`
  },
  methods: {
    ...mapActions(['refreshToken', 'validateToken']),
    async authenticate() {
      const token = cookies.get('EWS_AUTH_KEY')
      // Redirect to login app if it doesn't exist
      if (!token) {
        console.log('No token found, redirecting to login')
        return this.gotoLogin()
      }

      this.authMessage = 'Verifying user credentials ..'

      const api = axios.create({
        baseURL: `https://${apiDomain}`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        timeout: 60000
      })
      // if exired, try to refresh
      const payload = JSON.parse(atob(token.split('.')[1]))

      // check if token is expired otherwise validate
      // if either of these fail, user will be redirected to login
      if (payload?.exp < Date.now() / 1000) {
        console.warn('REFRESHING TOKEN')
        try {
          // token expired, try to refresh (this also validates the token)
          const refreshResponse = await this.refreshToken()
          this.isLoggedIn = true
          this.authInvalid = false
          this.$emit('authVerified', { data: refreshResponse?.data?.jwt })
        } catch (error) {
          console.error('Error refreshing token', error)
          cookies.remove('EWS_AUTH_KEY', { path: '/', domain: `.${baseDomain}` })
          this.authInvalid = true
          this.isLoggedIn = false
          return
        }
      } else {
        // no need to refresh, just validate the token
        const validateResponse = await this.validateToken()

        this.isLoggedIn = true
        this.authInvalid = false
        this.$emit('authVerified', { data: validateResponse?.data?.payload })
      }
    },
    gotoLogin() {
      window.location.href = this.loginUrl
    }
  }
}
</script>

<style>
.form-textarea,
.form-input {
  border-color: #e8e8e8;
}
</style>
